import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import { Cell } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { getPayroll } from '../../api/payroll';
import ForecastSocialTab from '../ForecastSocialTab';
import { getForecastAssumptions, putForecastAssumption } from '../../api/forecastAssumptions';
import '../ForecastCashPlan.css';
import './PayrollPage.css';

/* FRAIS DE PERSONNEL (Ventes & Ops › Finance & RH, proto validé le
   2026-09-28). Deux sources, la même que la ligne Salaires du plan de
   liquidité : People, qui se lit, ou Manuel, où l'on tape le brut, les
   13e / 14e et les primes. Le brut d'un mois vaut pour les suivants. Le
   calcul (déductions, net, charges employeur, charges sociales payées) vit au
   serveur, avec les taux et l'échéancier des Paramètres › Assurances sociales.

   PRO (People + Finance), 2026-09-29 : chez un client qui a la comptabilité,
   les mois clos lisent le RÉALISÉ comptable (brut, charges employeur) et
   People ne projette que les mois à venir. Depuis le 2026-09-30, avec
   Finance, le compte de résultat fait foi sur les douze mois ; la carte
   « Données People », SOUS la table, montre ce que People calcule et l'écart,
   People moins compte de résultat. */

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function PayrollPage({ activeTenant, year, addToast }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [v, setV] = useState(null);
  const [assum, setAssum] = useState({});
  const [unit, setUnit] = usePersistentValue(`payroll_unit_${tid}`, 'kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);

  const load = useCallback(async () => {
    if (!tid || !year) return;
    const [r, a] = await Promise.all([getPayroll(tid, year), getForecastAssumptions(tid, year)]);
    setV(r && !r.error ? r.data : null);
    setAssum((a && !a.error && a.data) || {});
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const exp = useViewExcelExport({ fileBase: p('Frais de personnel'), tenantName: activeTenant?.name, year, addToast });
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: exp.run }], [exp.run]);
  useRegisterPageExports(exportOptions);

  /* CONNECTER À PEOPLE, OU NON (décision Olivier, 2026-09-30) : le RÉALISÉ
     et le PRÉVISIONNEL, chacun « Saisi | Connecté à People ». Connecté, un
     mois lit People et reste modifiable (↺ remet People). En Pro, le réalisé
     vient de la comptabilité : seul le prévisionnel se connecte. */
  const align = v?.align || null;
  const pro = v?.source === 'finance';
  const setAlign = useCallback(async (key, val) => {
    if (!tid || !align) return;
    await putForecastAssumption(tid, year, 'payroll_align', { ...align, [key]: val === 'people' });
    await load();
  }, [tid, year, align, load]);
  const views = useMemo(() => {
    if (!align) return null;
    const hasActual = !pro && (Number(v?.closed_count) || 0) > 0;
    /* SANS LE MODULE PEOPLE (2026-09-30), l'option se lit grisée, cadenas et
       raison au survol : elle existe, le client ne l'a pas. */
    const rh = (activeTenant?.enabled_modules || []).includes('rh');
    const opt = [{ id: 'typed', label: p('Saisi') },
      rh ? { id: 'people', label: p('Connecté à People') }
        : { id: 'people', label: <span className="lk-lockopt">{p('Connecté à People')}<Lock size={11} aria-hidden="true" /></span>,
          disabled: true, hint: p('Demande le module People') }];
    return [
      ...(hasActual ? [{ id: 'actual', kind: 'segmented', label: p('Réalisé'), value: align.actual ? 'people' : 'typed',
        onChange: (x) => setAlign('actual', x), options: opt }] : []),
      { id: 'forecast', kind: 'segmented', label: p('Prévisionnel'), value: align.forecast ? 'people' : 'typed',
        onChange: (x) => setAlign('forecast', x), options: opt },
    ];
  }, [align, pro, v?.closed_count, p, setAlign, activeTenant?.enabled_modules]);

  const put = async (key, value) => {
    await putForecastAssumption(tid, year, key, value);
    await load();
  };
  const saveCell = (row, m, chf) => {
    const cur = (assum.payroll_manual && typeof assum.payroll_manual === 'object') ? assum.payroll_manual : {};
    const r = { ...(cur[row] || {}) };
    if (chf == null) delete r[String(m)]; else r[String(m)] = Math.abs(chf);
    return put('payroll_manual', { ...cur, [row]: r });
  };

  if (!tid) return null;
  const div = unit === 'chf' ? 1 : 1000;
  // Un montant qui s'arrondit à zéro dans l'unité se lit « – », jamais « −0 ».
  const rnd = (x) => Math.round(Number(x) / div);
  const fmt = (x) => (x == null || rnd(x) === 0 ? '–' : rnd(x).toLocaleString('de-CH'));
  const canType = (row) => !!v && (v.editable || []).includes(row);
  const typed = (row, m) => {
    const t = v?.typed?.[row] || {};
    return t[String(m)] != null ? Number(t[String(m)]) : null;
  };
  const sum = (arr) => (arr || []).reduce((s, x) => s + (Number(x) || 0), 0);
  // Réalisé comptable devant, prévisionnel ensuite : bandeaux et filet.
  const closed = Number(v?.closed_count) || 0;
  const split = !!v?.actuals && closed > 0 && closed < 12;
  const edge = (m) => (split && m === closed + 1 ? ' pay-edge' : '');

  const readRow = (label, arr, cls = '', sg = 1) => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {MONTHS.map((m) => {
        const x = sg * (Number(arr?.[m - 1]) || 0);
        return <td key={m} className={`fcp-num${x < 0 ? ' is-neg' : ''}${edge(m)}`}>{fmt(x)}</td>;
      })}
      <td className="fcp-num fcp-total">{fmt(sg * sum(arr))}</td>
    </tr>
  );
  // L'écart des mois clos : People moins la comptabilité, vide au-delà.
  const gapRow = (label, arr, cls = '') => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {MONTHS.map((m) => {
        const g = arr?.[m - 1];
        return <td key={m} className={`fcp-num${g != null && rnd(g) < 0 ? ' is-neg' : ''}${edge(m)}`}>{g == null ? '' : fmt(g)}</td>;
      })}
      <td className="fcp-num fcp-total">{fmt(sum(arr))}</td>
    </tr>
  );
  /* Une ligne à saisir : le brut affiche ce qui vaut ce mois (reporté), la
     cellule tapée se distingue ; 13e / 14e et primes ne valent que leur mois. */
  const inputRow = (label, row) => (
    <tr>
      <td className="fcp-lbl">{label}</td>
      {MONTHS.map((m) => {
        const shown = Number(v.rows?.[row]?.[m - 1]) || 0;
        const own = typed(row, m);
        if (m <= closed && v.actuals) {
          return <td key={m} className={`fcp-num${edge(m)}`}>{fmt(shown)}</td>;
        }
        /* CONNECTÉ À PEOPLE (2026-09-30) : un mois tapé sur une ligne que
           People nourrit se voit modifié, et ↺ lui rend la valeur de People. */
        const linked = row !== 'bonus' && v.aligned?.[m - 1];
        const mod = linked && own != null;
        const pv = linked && v.people ? Number(v.people[row]?.[m - 1]) || 0 : null;
        return (
          <td key={m} className={`fcp-num fcp-editcell fcp-manual${own != null ? ' is-edited' : ''}${mod ? ' pay-mod' : ''}`}
              title={mod ? `${p('Modifié')} · People ${fmt(pv)}` : undefined}>
            <Cell value={shown ? Math.round(shown / div) : null} type="number" canWrite placeholder="–"
                  format={(x) => (x == null ? '' : fmt(x * div))}
                  onSave={(x) => saveCell(row, m, x == null ? null : x * div)} />
            {mod && (
              <button type="button" className="pay-reset" aria-label={p('Remettre la valeur de People')}
                      onClick={() => saveCell(row, m, null)}>↺</button>
            )}
          </td>
        );
      })}
      <td className="fcp-num fcp-total">{fmt(sum(v.rows?.[row]))}</td>
    </tr>
  );

  return (
    <PageShell kind="entry" title={p('Salaires prévisionnels')} unit={pageUnit} views={views} exportRef={exp.ref}>
      {!v ? <p className="fcp-loading">{p('Chargement…')}</p> : (
        <CardShell title={p('Salaires et charges sociales')} subtitle={`${year} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`}
                   flush>
          <div className="fcp-wrap">
            <table className="fcp fcp--soft">
              <thead>
                {split && (
                  <tr>
                    <th />
                    <th className="lk-band lk-band--real" colSpan={closed}>{p('Réalisé')}</th>
                    <th className="lk-band lk-band--fore" colSpan={12 - closed}>{p('Prévisionnel')}</th>
                    <th />
                  </tr>
                )}
                <tr>
                  <th className="fcp-lbl" />
                  {MONTHS.map((m) => <th key={m} className={`fcp-num${edge(m)}`}>{MOIS[m - 1]}</th>)}
                  <th className="fcp-num fcp-total">{p('Total')}</th>
                </tr>
              </thead>
              <tbody>
                {/* LA MÊME TABLE QUELLE QUE SOIT LA SOURCE (2026-09-30) : ce qui
                    se tape porte le fond de saisie, le reste se lit. */}
                {canType('base') ? inputRow(p('Salaire brut'), 'base') : readRow(p('Salaire brut'), v.rows?.base)}
                {canType('extras') ? inputRow(p('13e / 14e'), 'extras') : readRow(p('13e / 14e'), v.rows?.extras)}
                {canType('bonus') ? inputRow(p('Primes'), 'bonus') : readRow(p('Primes'), v.rows?.bonus)}
                {readRow(p('Masse salariale brute'), v.gross, 'fcp-sub')}
                {readRow(`− ${p('Déductions salariés')}`, v.deductions, '', -1)}
                {readRow(p('Salaires nets versés'), v.net, 'fcp-sub')}
                {readRow(p('Charges employeur'), v.employer)}
                {readRow(p('Charges sociales payées'), v.invoices, 'fcp-sub')}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
      {v && v.people && v.gap && (
        /* DONNÉES PEOPLE, EN INFORMATION (décision Olivier, 2026-09-30) : ce que
           People calcule (salaire brut, 13e / 14e, charges employeur), puis
           l'écart au compte de résultat, dans un sens dit en toutes lettres.
           Elle passe AVANT le détail des charges sociales (2026-10-03) : l'écart
           se lit juste sous la table qu'il compare. */
        <CardShell title={p('Données calculées selon le module People')} subtitle={`${year} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`} flush>
          <div className="fcp-wrap">
            <table className="fcp fcp--soft">
              <thead>
                <tr>
                  <th className="fcp-lbl" />
                  {MONTHS.map((m) => <th key={m} className={`fcp-num${edge(m)}`}>{MOIS[m - 1]}</th>)}
                  <th className="fcp-num fcp-total">{p('Total')}</th>
                </tr>
              </thead>
              <tbody>
                {gapRow(p('Salaire brut'), v.people.base)}
                {gapRow(p('13e / 14e'), v.people.extras)}
                {gapRow(p('Masse salariale brute'), v.people.gross, 'fcp-sub')}
                {gapRow(p('Charges employeur'), v.people.employer)}
                <tr className="pay-head"><td className="fcp-lbl" colSpan={14}>{v.source === 'finance' ? p('Écart People − compte de résultat, hors primes') : p('Écart People − saisie, hors primes')}</td></tr>
                {gapRow(p('Masse salariale brute'), v.gap.gross)}
                {gapRow(p('Charges employeur'), v.gap.employer)}
                {gapRow(p('Total'), MONTHS.map((m) => (Number(v.gap.gross[m - 1]) || 0) + (Number(v.gap.employer[m - 1]) || 0)), 'fcp-sub')}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
      {v && v.social && v.social.charges.length > 0 && (
        /* LE DÉTAIL DES CHARGES SOCIALES, LA VUE DE PEOPLE (2026-09-30) : même
           composant, en trois groupes repliables (cotisations de base,
           assurances, LPP), calculé sur la table du dessus ; le brut et le net
           n'y figurent pas, la table les porte. */
        <ForecastSocialTab plan={v.social} hideSalaries title={p('Détail des charges sociales')}
                           activeTenant={activeTenant} year={year} unit={unit} />
      )}
    </PageShell>
  );
}
